import {Link} from 'react-router';
import {SummaryStrip} from '../ui/SummaryStrip';
import {goLiveCountdown} from '../ui/go-live';
import {withBuildingScope} from '../ui/building-scope';
import {OnboardingProgress} from '../ui/OnboardingProgress';
import type {BuildingOnboardingProgress} from '../ui/building-onboarding-progress';
import {useBuildingOnboardingProgress} from '../ui/useBuildingOnboardingProgress';

/** One row of the incomplete setup summary, independent of operational management status. */
export type OnboardingRow={development_id:string;go_live_date?:string|null;completed_step_count:number;total_step_count:number;blocked_step_count:number;operational?:{status:string;managementStart?:string|null}};
export type OnboardingProgress=Map<string,OnboardingRow>;

type OverviewProps={onboarding?:OnboardingProgress|undefined;unavailable:boolean;only:boolean;onOnlyChange:(only:boolean)=>void;filterOnly?:boolean;summaryOnly?:boolean};
/** Summary strip and "Needs onboarding" filter shown above the Buildings register. */
export function OnboardingOverview({onboarding,unavailable,only,onOnlyChange,filterOnly=false,summaryOnly=false}:OverviewProps){
 if(unavailable)return <p role="status" className="form-help">Onboarding progress could not be read. The building list below is unaffected.</p>;
 if(!onboarding)return null;
 const rows=[...onboarding.values()];
 return <>
  {!filterOnly&&<SummaryStrip mode="metric" ariaLabel="Onboarding summary" items={[
   {label:'Onboarding incomplete',value:rows.length},
   {label:'Scheduled',value:rows.filter(row=>row.operational?.status==='Scheduled').length},
   {label:'With blockers',value:rows.filter(row=>row.blocked_step_count>0).length},
  ]}/>}
  {!summaryOnly&&<p><label>Show <select value={only?'onboarding':'all'} onChange={event=>onOnlyChange(event.target.value==='onboarding')}>
   <option value="all">All buildings</option><option value="onboarding">Needs onboarding ({rows.length})</option>
  </select></label></p>}
 </>;
}

/** The Onboarding column cell for one building. */
export function OnboardingStatus({building,step,loaded,journey}:{building:string;step?:OnboardingRow|undefined;loaded:boolean;journey:string}){
 if(!step)return loaded?<span aria-label="Not being onboarded">{'\u2014'}</span>:null;
 const blocked=step.blocked_step_count?`${step.blocked_step_count} blocked`:'No blockers';
 return <>
  <Link to={withBuildingScope(`/buildings/${building}/${journey}`,building)}>Onboarding: {step.completed_step_count} of {step.total_step_count} steps</Link>
  <small>{blocked} {'\u00b7'} {step.operational?.status==='Live'?'Management live · setup incomplete':goLiveCountdown(step.go_live_date??step.operational?.managementStart)}</small>
 </>;
}

export function OnboardingCell(props:Parameters<typeof OnboardingStatus>[0]){return <td data-label="Onboarding"><OnboardingStatus {...props}/></td>;}

/** Owner feedback item 5 (12 Oct 2026): the first card on an incomplete building's Overview leads back into its onboarding journey. */
export function BuildingOnboardingCard({progress}:{progress:BuildingOnboardingProgress|undefined}){
 if(!progress)return null;
 return <section className="building-onboarding-card" aria-label="Onboarding">
  <div><h2>{progress.href.includes("/genie")?"Setup with Genie":"Onboarding"}</h2>
   <OnboardingProgress completed={progress.completed} total={progress.total} label="Onboarding progress" unit="steps"/>
   <p>{progress.blocked?`${progress.blocked} blocked`:'No blockers'} {'\u00b7'} {progress.managementLive?'Management live · setup incomplete':goLiveCountdown(progress.goLive)}</p>
   {progress.next&&<p>Next step: {progress.next}</p>}</div>
  <Link className="button-link" to={progress.href}>{progress.href.includes("/genie")?"Continue setup with Genie":"Continue onboarding"}</Link>
 </section>;
}

/** The Overview card for the building being viewed; the building tab bar shares the same summary read. */
export function BuildingOverviewOnboarding({building}:{building:string}){
 const progress=useBuildingOnboardingProgress(building,true);
 return <BuildingOnboardingCard progress={progress}/>;
}
